<template>
  <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" :close-on-click-modal="false" width="640px"
    append-to-body class="contract-form-dialog" @closed="onDialogClosed">
    <el-descriptions :column="2" border size="medium" :label-style="{ width: '140px' }"
      :content-style="{ width: '160px' }" class="contract-info">
      <el-descriptions-item label="Bankholder ID">{{ form.bankholderID }}</el-descriptions-item>
      <el-descriptions-item label="Currency">{{ currency.toUpperCase() }}</el-descriptions-item>
      <template v-if="mode === 'renew' && activeContractInfo">
        <el-descriptions-item label="Status">
          <el-tag type="success" size="mini" class="status-tag">{{ activeContractInfo.status }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="Duration">{{ monthsText(activeContractInfo.duration_months) }}</el-descriptions-item>
        <el-descriptions-item label="Total Amount">{{ moneyText(activeContractInfo.total_amount) }}</el-descriptions-item>
        <el-descriptions-item label="Contract Balance">
          {{ activeContractInfo.last_state ? moneyText(activeContractInfo.last_state.balance) : "-" }}
        </el-descriptions-item>
        <el-descriptions-item v-if="activeContractInfo.last_state" label="Last Activity">
          {{ activeContractInfo.last_state.description }}
        </el-descriptions-item>
      </template>
    </el-descriptions>

    <el-form ref="contractForm" :model="form" :rules="formDataRules" label-position="left" label-width="140px"
      class="duration-form" @submit.native.prevent>
      <el-form-item :label="mode === 'renew' ? 'Extend Duration' : 'Duration'" prop="durationMonths">
        <el-input-number v-model="form.durationMonths" controls-position="right" :min="1" :max="120"
          @change="scheduleEstimate"></el-input-number>
        <span class="duration-unit">months</span>
      </el-form-item>
    </el-form>

    <div class="charge-summary">
      <div class="charge-row">
        <span class="charge-label">Monthly Charge</span>
        <span class="charge-value" :class="{ 'is-stale': estimateLoading }">{{ monthlyText }}</span>
      </div>
      <div class="charge-row charge-total">
        <span class="charge-label">{{ mode === 'renew' ? 'Renewal Charge' : 'Total Charge' }}</span>
        <span class="charge-value">
          <i v-if="estimateLoading" class="el-icon-loading"></i>
          <span :class="{ 'is-stale': estimateLoading }">{{ moneyText(chargeAmount) }}</span>
        </span>
      </div>
      <div v-if="balanceLoading || balance !== null" class="charge-balance">
        <div class="charge-row">
          <span class="charge-label">Account Balance</span>
          <span class="charge-value">
            <i v-if="balanceLoading" class="el-icon-loading"></i>
            <template v-else>{{ moneyText(balance) }}</template>
          </span>
        </div>
        <div v-if="!balanceLoading && balanceAfter !== null" class="charge-row"
          :class="{ 'is-negative': insufficientBalance }">
          <span class="charge-label">Balance After Charge</span>
          <span class="charge-value" :class="{ 'is-stale': estimateLoading }">{{ moneyText(balanceAfter) }}</span>
        </div>
      </div>
    </div>
    <p class="charge-note">The full amount is charged from this account when you confirm.</p>

    <el-alert v-if="chargeAlert" :title="chargeAlert.title" :type="chargeAlert.type" :closable="false" show-icon
      class="charge-alert"></el-alert>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">Cancel</el-button>
      <el-button type="primary" :loading="submiting" :disabled="chargeBlocked" @click="submitForm">Confirm</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { createContract, renewContract, estimateCreateContract, estimateRenewContract } from "@/api/customer";
import { getBankAccountBy } from "@/api/bank-account";
import { currencyFormat } from "@/utils";
export default {
  name: "ContractForm",
  data() {
    return {
      submiting: false,
      dialogVisible: false,
      mode: "create", // create | renew
      currency: "",
      activeContractInfo: null, // renew: item from getContractList
      form: {
        "type": "monthly", // or "compliance"
        "bankholderID": "",
        "contractUID": "", // renew only
        "durationMonths": 6
      },
      // charge estimate of current months (server re-checks price and balance on confirm)
      estimate: null,
      estimateLoading: false,
      estimateError: "", // server rejected (result=false) -> block confirm
      estimateUnavailable: false, // request failed (network / server without estimate API) -> confirm still allowed
      estimateSeq: 0, // only the latest request may update the state
      estimateTimer: null,
      balance: null, // bank account balance, null = unknown (don't block)
      balanceLoading: false,
      balanceSeq: 0,
      formDataRules: {
        "durationMonths": [
          { required: true, message: "Please enter duration", trigger: "blur" },
          { type: "number", min: 1, max: 120 }
        ],
      },
    }
  },
  beforeDestroy() {
    clearTimeout(this.estimateTimer);
  },
  computed: {
    dialogTitle() {
      return `${this.mode === "renew" ? "Renew" : "Create"} ${this.typeLabel(this.form.type)} Contract Agreement`;
    },
    monthlyAmount() {
      return this.estimate ? this.toAmount(this.estimate.monthlyAmount) : null;
    },
    monthlyText() {
      if (this.monthlyAmount === null) return "-";
      return `${this.moneyText(this.monthlyAmount)} × ${this.monthsText(this.estimate.durationMonths || this.form.durationMonths)}`;
    },
    // create charges totalAmount, renew charges additionalAmount (both charged at confirm)
    chargeAmount() {
      if (!this.estimate) return null;
      return this.toAmount(this.mode === "renew" ? this.estimate.additionalAmount : this.estimate.totalAmount);
    },
    balanceAfter() {
      if (this.balance === null || this.chargeAmount === null) return null;
      return Math.round((this.balance - this.chargeAmount) * 100) / 100;
    },
    // same comparison as server checkBalanceEnough (balance >= amount)
    insufficientBalance() {
      return this.balance !== null && this.chargeAmount !== null && this.balance < this.chargeAmount;
    },
    chargeBlocked() {
      return this.estimateLoading || !!this.estimateError || this.insufficientBalance;
    },
    chargeAlert() {
      if (this.estimateLoading) return null;
      if (this.estimateError) {
        return { type: "error", title: this.estimateError };
      }
      if (this.insufficientBalance) {
        return { type: "error", title: "Insufficient balance to cover this charge" };
      }
      if (this.estimateUnavailable) {
        return { type: "warning", title: "Unable to estimate the charge right now, server will calculate it on confirm" };
      }
      return null;
    },
  },
  methods: {
    typeLabel(type) {
      return type === 'monthly' ? 'Monthly' : 'Compliance';
    },
    // finite number or null (unknown)
    toAmount(value) {
      if (value === null || value === undefined || value === "") return null;
      const amount = Number(value);
      return Number.isFinite(amount) ? amount : null;
    },
    monthsText(months) {
      return Number(months) === 1 ? "1 month" : `${months} months`;
    },
    moneyText(value) {
      const amount = this.toAmount(value);
      return amount === null ? "-" : `${currencyFormat(amount)} ${this.currency.toUpperCase()}`;
    },
    resetCharge() {
      clearTimeout(this.estimateTimer);
      this.estimateSeq++;
      this.balanceSeq++;
      this.estimate = null;
      this.estimateLoading = false;
      this.estimateError = "";
      this.estimateUnavailable = false;
      this.balance = null;
      this.balanceLoading = false;
    },
    // fired after close animation: skip if the dialog was opened again meanwhile
    onDialogClosed() {
      if (!this.dialogVisible) this.resetCharge();
    },
    // months changed: estimate again after user stops clicking
    scheduleEstimate() {
      clearTimeout(this.estimateTimer);
      this.estimateLoading = true;
      this.estimateTimer = setTimeout(() => this.loadEstimate(), 400);
    },
    // never rejects
    loadEstimate() {
      clearTimeout(this.estimateTimer);
      const seq = ++this.estimateSeq;
      const months = this.form.durationMonths;
      if (!Number.isInteger(months) || months < 1) {
        this.estimate = null;
        this.estimateLoading = false;
        this.estimateError = "";
        this.estimateUnavailable = false;
        return Promise.resolve();
      }
      this.estimateLoading = true;
      const request = this.mode === "renew"
        ? estimateRenewContract({ contractUID: this.form.contractUID, durationMonths: months })
        : estimateCreateContract({ bankholderID: this.form.bankholderID, type: this.form.type, durationMonths: months });
      return request.then(({ result, message, data }) => {
        if (seq !== this.estimateSeq) return;
        this.estimate = result && data ? data : null;
        this.estimateError = result ? "" : (typeof message === "string" && message ? message : "Unable to estimate contract charge");
        this.estimateUnavailable = false;
      }).catch(() => {
        if (seq !== this.estimateSeq) return;
        this.estimate = null;
        this.estimateError = "";
        this.estimateUnavailable = true;
      }).then(() => {
        if (seq === this.estimateSeq) this.estimateLoading = false;
      });
    },
    // same ledger balance the server checks on confirm; never rejects
    loadBalance() {
      const seq = ++this.balanceSeq;
      this.balanceLoading = true;
      return getBankAccountBy(this.form.bankholderID, true).then(({ result, data }) => {
        if (seq !== this.balanceSeq) return;
        this.balance = result && data ? this.toAmount(data.balance) : null;
      }).catch(() => {
        if (seq === this.balanceSeq) this.balance = null;
      }).then(() => {
        if (seq === this.balanceSeq) this.balanceLoading = false;
      });
    },
    // price / balance / contract may change while dialog is open: check again right before confirm
    recheckCharge() {
      this.submiting = true;
      return Promise.all([this.loadEstimate(), this.loadBalance()]).then(() => {
        this.submiting = false;
        return !this.chargeBlocked;
      });
    },
    confirmMessage(action) {
      const h = this.$createElement;
      const lines = [];
      if (this.chargeAmount !== null) {
        lines.push(h("p", null, [
          "Charge ",
          h("strong", null, this.moneyText(this.chargeAmount)),
          ` (${this.monthlyText}) from bankholderID ${this.form.bankholderID}`
        ]));
      }
      lines.push(h("p", null, `Are you sure you want to ${action}, Continue?`));
      return h("div", null, lines);
    },
    // create new contract for a bank account
    showCreate(type, bankholderID, currency) {
      this.activeContractInfo = null;
      this.openDialog("create", { type, bankholderID, contractUID: "" }, currency);
    },
    // renew existing contract (item from getContractList)
    showRenew(contract) {
      this.activeContractInfo = contract;
      this.openDialog("renew", {
        type: contract.type,
        bankholderID: contract.bankholderID,
        contractUID: contract.contract_uid
      }, contract.currency);
    },
    openDialog(mode, target, currency) {
      this.mode = mode;
      this.form = { ...target, durationMonths: 6 };
      this.currency = currency || "";
      this.resetCharge();
      this.loadEstimate();
      this.loadBalance();
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.contractForm) this.$refs.contractForm.clearValidate();
      });
    },
    submitForm() {
      this.$refs.contractForm.validate((valid) => {
        if (valid) {
          this.recheckCharge().then((ok) => {
            if (!ok || !this.dialogVisible) return;
            const renew = this.mode === "renew";
            this.$confirm(this.confirmMessage(renew ? 'renew contract agreement' : 'make contract agreement'), 'Warning', {
              confirmButtonText: 'OK',
              cancelButtonText: 'Cancel',
              type: 'warning'
            }).then(() => {
              this.submiting = true;
              const request = renew
                ? renewContract({ contractUID: this.form.contractUID, durationMonths: this.form.durationMonths })
                : createContract({ bankholderID: this.form.bankholderID, type: this.form.type, durationMonths: this.form.durationMonths });
              request.then(({ result, message }) => {
                this.submiting = false;
                if (result) {
                  this.$notify({
                    title: "Success",
                    message: message,
                    type: "success",
                    duration: 2000
                  });
                  this.dialogVisible = false;
                  this.$emit("submited");
                }
              }).catch(() => {
                this.submiting = false;
              });
            }).catch(() => {
              this.submiting = false;
            });
          });
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.contract-form-dialog ::v-deep .el-dialog__body {
  padding: 10px 20px;
}

.contract-info {
  margin-bottom: 20px;

  .status-tag {
    text-transform: capitalize;
  }
}

// label text / input lines up with the table label / content text above
.duration-form {
  padding-left: 11px;

  .duration-unit {
    margin-left: 10px;
    color: #909399;
  }
}

.charge-summary {
  padding: 4px 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #f5f7fa;

  .charge-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 0;
    color: #606266;
    font-size: 14px;
    line-height: 20px;
  }

  .charge-value {
    color: #303133;
    font-variant-numeric: tabular-nums;

    .el-icon-loading {
      margin-right: 6px;
      color: #909399;
    }
  }

  .is-stale {
    opacity: 0.4;
  }

  .charge-total {
    border-top: 1px solid #dcdfe6;

    .charge-label {
      color: #303133;
      font-weight: 700;
    }

    .charge-value {
      font-size: 18px;
      font-weight: 700;
    }
  }

  .charge-balance {
    border-top: 1px dashed #dcdfe6;

    .charge-row {
      font-size: 13px;
    }

    .charge-value {
      color: #606266;
    }

    .is-negative .charge-value {
      color: #f56c6c;
      font-weight: 700;
    }
  }
}

.charge-note {
  margin: 8px 0 0;
  padding-left: 11px;
  color: #909399;
  font-size: 12px;
}

.charge-alert {
  margin-top: 12px;
}
</style>
